import { PaymentServiceProvider, PaymentToken, PickupPath } from '@app/core/models/payment-service-models.model';
import { OrderTotalLineItem } from '@app/shared/models/receipt.model';
import { CustomerInfo } from '@core/redux/stores/guest.store';
import { DriveThruLaneInfo, LocationsEntity } from '@core/redux/stores/location.store';
import { ChildRecipeEntity } from '@core/redux/stores/recipes.store';
import { TimePicker } from '@features/ordering/models';
import { OrderTriggerType } from '@ordering/utilties/enums/order-status.utilities';
import { Address } from '@shared/cms/models/group-dining-request';

export class CheckBreakfast {
  isConfirmAndPay: boolean;
  isOrderSummary: boolean;
}

export enum RecipeContext {
  CHILD = 1,
  LINKED = 2
}

export enum PickupType {
  DineIn = 1,
  DriveThru = 2,
  TakeOut = 3,
  Curbside = 4,
  Delivery = 5,
  NotListed = 0
}

export interface PickupTypeOptions {
  pickupType: PickupType;
  enabled: boolean;
  disabledReason?: DisabledReason;
  disabledText?: string;
  pickupTypeId?: string;
  sort: number;
}

export enum DisabledReason {
  NotAvailable = 1,
  MaxDeliveryAmount = 2,
  OnlyAvailableLater = 3,
  OnlyAvailableOffsite = 4,
  LocationTrackingDisabled = 5
}

export enum PickupTypeDisplayValue {
  DriveThru = 'Drive Thru',
  DineIn = 'Dine In',
  TakeOut = 'Take Out',
  Curbside = 'Curbside',
  Delivery = 'Delivery'
}

export enum ServiceArea {
  DineIn = 1,
  DriveThru = 2,
  DineInDriveThru = 3
}

export enum CheckInType {
  None = 0,
  Curbside = 1,
  CurbsideSpace = 2,
  CurbsideVehicle = 3,
  CurbsideSpaceUnavailable = 4,
  Table = 5
}

export class UserOrderInfo {
  orderNumber: number | null;
  pickupTime?: string | null;
  pickupDateTimeUtc?: string | null;
  status?: number | null;
  statusDateTimeUtc?: string | null;
  statusDateTime?: string | null;
  location?: LocationsEntity | null;
  id?: string | null;
  locationId?: number | null;
  pickupType?: number | null;
  isFavorite?: boolean | null;
  favoriteName?: string | null;
  sort?: number | null;
  createDateTimeUtc?: string | null;
  recipes?: (MealRecipe)[] | null;
  concurrencyStamp?: string | null;
  guid?: string | null;
  displayPickupTime?: string | null;
  quantity?: number | 0;
  orderTotal?: number | 0;
  orderTriggerType?: OrderTriggerType | 1;
  deliveryInfo?: OrderDeliveryInfo | null;
  subTotal?: number | 0;
  submitTimeStamp?: number | null;
  checkInInfo: CheckInInfo | null;
  customerInfo?: CustomerInfo;
  orderTotalLineItem?: OrderTotalLineItem[];
  asapPickupTime?: AsapPickupTime;
  driveThruLaneInfo?: DriveThruLaneInfo;
  foodLockerInfo?: FoodLockerInfo;
}

export interface FoodLockerInfo {
  status?: FoodLockerStatus;
  lastUpdatedUtc?: string;
  htmlContent?: string;
  content?: AttributedText[];
  imageUrl?: string;
  unlockCode?: string;
  qrCodeUrl?: string;
  isException?: boolean;
  title?: string;
}


export enum FoodLockerStatus {
  Pending = 1,
  Ready,
  PickedUp,
  Expired,
  Oversize,
  LockedDown,
  Removed,
  Reclaimed,
  Error,
  OversizeReady
}

export interface AttributedText {
  text: string
  bold: boolean
  italic: boolean
  color: string
  underline: boolean;
  strikethrough: boolean;
  backgroundColor: string;
  fontSize: number | null;
}

export interface AsapPickupTime {
  dropTime?: DateTimeLocal;
  pickupStartTime?: DateTimeLocal;
  pickupEndTime?: DateTimeLocal;
  asapTimeRange?: AsapTimeRange;
}

export interface DateTimeLocal {
  local: string;
  utc: string;
}

export interface AsapTimeRange {
  min: number;
  max: number;
}

export class CheckInInfo {
  isCheckInRequired: boolean;
  checkInType?: CheckInType;
  checkedInTimeUtc?: Date | string | null;
  checkInIndicator: string;
  checkInExpirationTimeUtc?: Date | string;
}

export interface OrderCheckInRequest {
  checkInType: CheckInType;
  checkInIndicator: string;
  checkInVehicleDetails?: CheckInVehicleDetails;
}

export interface CheckInVehicleDetails {
  vehicleColorId: number;
  vehicleTypeId: number;
}

export class Order {
  childRecipes?: (ChildRecipeEntity)[] | null;
  isFavorite = false;
  favoriteName: string;
  // TODO the payment stuff listed below is NOT COMPLETE. This is generic scaffolding.
  // If payment requires a specific object, then make it here to be use.
  pickupType: PickupType;
  paymentToken: string;
  paymentInfo: any;
  total: number | null;
  tax: number | null;
  scheduledTime: string;
  scheduledEndDate: string;
  isRewardAdded = false;
  pickupTime?: Date | null;
  isNudgeClosed = false;
  tipAmount?: number;
  deliveryQuote?: DeliveryQuote;
  deliveryAddress?: DeliveryAddress;
  deliveryTimeUtc?: string;
  space?: string;
  automaticDeliveryLocation? = false;
  pickupPath?: PickupPath;
}

export class MealRecipe {
  clientId: string | null;
  categoryId: number | null;
  recipeId: number | null;
  recipeTypeId: number | null;
  customName: string | null;
  quantity: number | null;
  useReward: boolean | null;
  offerId: Number | null;
  useWhatASize: boolean | null;
  customizations: (MealRecipeCustomization)[] | null;
  whatsOnIt: string | null;
  subTitle: string | null;
  totalPrice: number | null;
  comesWith: string | null;
  totalCalories: number | null;
  name: string | null;
  isOfferAdded: boolean | false;
  offerCouponCode: Number | null;
  isAttachToOffer: boolean | false;
  offerFlag: boolean | false;
}

export class MealRecipeCustomization {
  customizationGroupId: number | null;
  modifierGroupId: number | null;
  modifierId: number | null;
  specialInstructionIds?: (number)[] | null;
}

export class Meal {
  id: string;
  locationId: number;
  location: LocationsEntity;
  pickupType: number;
  isFavorite: boolean;
  favoriteName: string;
  sort: number;
  createDateTimeUtc: string;
  recipes?: (Recipe)[];
  concurrencyStamp: string;
  mealId?: string;
}

export class Recipe {
  clientId: string;
  categoryId: number;
  recipeId: number;
  recipeTypeId: number | null;
  customName: string;
  quantity: number;
  useReward: boolean;
  offerId: Number;
  useWhatASize: boolean;
  customizations?: (Customizations)[];
  isOfferAdded: boolean | false;
}

export class Customizations {
  customizationGroupId: number;
  modifierGroupId: number;
  modifierId: number;
  imageUrl?: string | null;
  specialInstructionIds: number[];
  linkedRecipeId: number | null;
  childCustomizations?: Customizations[];
}

export class SubmitOrderRequest {
  customerInfo: CustomerInfo | null;
  meal: Meal | null;
  isOrderUpdate: boolean | null;
  pickupDateTimeUtc: string | null;
  paymentInfo: OrderPaymentInfo | null;
  originPoint: number | 1;
  triggerType: OrderTriggerType | 1;

  clientDateTime: string;
  confirmAndPay: ConfirmAndPay;
  selectedTime: TimePicker;
  deliveryInfo?: DeliveryInfo;
  isPushEnabled?: boolean;
}

export class OrderPaymentInfo {
  paymentType: number | null;
  paymentTokenOrId?: string | null;
  paymentCardType?: string;
  cardLastFourDigits?: string | null;
  cardBrand?: string | null;
  paypalAuthorizationCode?: string | null;
  saveForFuturePayment: boolean;
  tokenType?: number | null;
  token: string | null;
  paymentServiceProvider?: PaymentServiceProvider | null;
  paymentToken?: PaymentToken | null;
  fraudDataCollectorSessionId: string | null;
}

export class ConfirmAndPay {
  showtimepicker: boolean;
  showpickupTime: boolean;
  showpickupMethod: boolean;
  showmethodpicker: boolean;
  pickupmethod: string;
  showpickuplocation: boolean;
  showcontactinfo: boolean;
  showPlaceOrder: boolean;
  showPaypal: boolean;
  disablePickUpMethod: boolean;
  showpaymentinfo: boolean;
  showAddpayment: boolean;
  showordersummary: boolean;
  headerType: number;
  isLocationChange: boolean;
  showApplePay: boolean;
  showGooglePay: boolean;
}

/* creating temporary payment object for Guest user as the existing object property type is not matching with Guest payment DTO.
I hope we can reuse the same object for both signed in and Guest users. If not will move this to guest.store.ts */
export class OrderGuestPaymentInfo {
  paymentType: number | null;
  paymentTokenOrId?: string | null;
  paymentCardType: number | null;
  cardLastFourDigits?: string | null;
  brand?: string | null;
  payPalAuthcode?: string | null;
  saveForFuturePayment: boolean;
  token: string | null;
  tokenType: number | null;
  isDefault: boolean | false;
  paymentToken?: PaymentToken | null;
}


export class DeliveryAddress extends Address {
  id?: number;
  instructions: string | null;
  phoneNumber: string | null;
  title: string | null;
  isSaveAddress?: boolean | null;
}

export class DeliveryQuoteRequest {
  dropoffAddress: DeliveryAddress;
  deliveryTimeUtc: String;
  pickupLocationId: number;
  orderSubtotal: number;
  isOversize: boolean;
}

export interface DeliveryQuote {
  isAvailable: boolean;
  quoteId: string;
  serviceFee: DeliveryFee;
  deliveryFee: DeliveryFee;
  isTippingAllowed: boolean;
  isTipTaxable: boolean;
  tipFee?: DeliveryFee;
  estimatedPickupTime: Date | string;
  estimatedDropoffTime: Date | string;
  expiresAtUtc: Date | string;
  location?: LocationsEntity;
  isOversize: boolean;
}

export interface DeliveryFee {
  amount: number;
  discounts: DeliveryDiscount[];
  isTaxable: boolean;
}

export interface DeliveryDiscount {
  minimumSubTotal: number;
  maximumSubTotal: number | null;
  discountAmount: number;
}

export interface DeliveryInfo {
  quoteId: string;
  dropoffName: string;
  dropoffPhoneNumber: string;
  tipAmount: number;
}

export interface PickupTypeMetadata {
  text: string;
  pickupType: PickupType;
  serviceAreas: ServiceArea[];
  amenities: string[];
  fallbackPickupTypes: PickupType[];
  pointOfSalePickupTypes: PickupType[];
  idText: string;
  iconClass: string;
  labelText?: string;
  sort: number;
}

export interface OrderDeliveryInfo {
  deliveryAddress: DeliveryAddress;
  status: number;
  statusDateTimeUtc: Date | string;
  estimatedDeliveryTimeUtc: Date | string;
  estimatedDeliveryTime: Date | string;
  displayEstimatedDeliveryTime: string;
  driverInfo: DriverInfo;
}

export interface DriverInfo {
  name: string;
  phoneNumber: string;
  coordinates: Coordinates;
}

export interface Coordinates {
  longitude?: number;
  latitude?: number;
}

export interface OrderCheckInOption {
  locationId: number;
  allowSpecifyVehicleType: boolean;
  allowChangeCheckIn: boolean;
  allowXenialTableTentCheckIn: boolean;
  allowDuplicateCheckinsOnHUD: boolean;
  allowRemoveHudOrder: boolean;
  isCurbsideCheckInEnabled: boolean;
  isTableCheckInEnabled: boolean;
  checkInVehicleColors: CheckInVehicleColor[];
  checkInVehicleTypes: CheckInVehicleType[];
}

export interface CheckInOptions extends OrderCheckInOption {
  maxTableCapacity: number | null;
  tables: string[];
  parkingSpaces: string[];
  alternatePickupTypes: PickupType[];
}

export interface CheckInVehicleColor {
  id: number;
  vehicleColor: string;
  vehicleColorAbbreviation: string;
}

export interface ChangePickupTypeRequest {
  pickupType: PickupType;
}

export interface CheckInVehicleType {
  id: number;
  vehicleType: string;
  vehicleTypeAbbreviation: string;
}

export interface ValidateAddressRequest {
  address: Address;
}

export interface ValidateAddressResponse {
  inputAddress: Address;
  standardizedAddress: Address;
}
